import React from 'react'

import type { IconComponent } from '@fourthwall/components'
import { Dropdown, Icon } from '@fourthwall/components'
import {
  ArrowShortLeftIcon,
  CloseIcon,
  DotsVerticalIcon,
  DownloadIcon,
  PreviewOffIcon,
  PreviewOnIcon,
  RefreshIcon,
} from '@fourthwall/icons'

import type { ThankYou } from '../../../../ThankYous.types'
import type { Media } from '../../SendThankYouModal.types'
import { IconButton } from '../IconButton/IconButton'
import * as S from './TopBar.styled'

export type TopBarProps = {
  thankYou: ThankYou
  media: Media
  isSuccess: boolean
  isUploading: boolean
  showOrderDetails: boolean
  onToggleShowOrderDetails: () => void
  onSkip?: () => void
  onDownload?: () => void
  onClose: () => void
}

export const TopBar = ({
  thankYou,
  media,
  isSuccess,
  isUploading,
  showOrderDetails,
  onToggleShowOrderDetails,
  onSkip,
  onClose,
  onDownload,
}: TopBarProps) => {
  const renderLeftSide = () => {
    if (thankYou.state === 'SENT' || isSuccess || isUploading) return null

    if (media.status === 'PREVIEW') {
      return <IconButton onClick={media.onRecordDelete} component={ArrowShortLeftIcon} />
    }

    return <S.Nickname>{thankYou.supporter.fullName}</S.Nickname>
  }

  const renderCloseButton = () => {
    if ((media.status === 'PREVIEW' && thankYou.state !== 'SENT' && !isSuccess) || isSuccess)
      return null

    return <IconButton component={CloseIcon} onClick={onClose} />
  }

  const renderDropdownItemLabel = (icon: IconComponent, label: string) => (
    <>
      <div style={{ marginRight: 16 }}>
        <Icon component={icon} height={16} />
      </div>{' '}
      {label}
    </>
  )

  const renderDropdown = () => {
    if (
      (media.status !== 'IDLE' && thankYou.state !== 'SENT') ||
      (thankYou.state === 'SENT' && !onDownload) ||
      isSuccess
    )
      return null

    return (
      <Dropdown
        content={
          thankYou.state === 'SENT' ? (
            onDownload && (
              <Dropdown.Item
                label={renderDropdownItemLabel(DownloadIcon, 'Download')}
                onClick={onDownload}
              />
            )
          ) : (
            <>
              <Dropdown.Item
                label={renderDropdownItemLabel(
                  showOrderDetails ? PreviewOffIcon : PreviewOnIcon,
                  `${showOrderDetails ? 'Hide' : 'Show'} order details`,
                )}
                onClick={onToggleShowOrderDetails}
              />
              {onSkip && (
                <Dropdown.Item
                  label={renderDropdownItemLabel(RefreshIcon, 'Skip this customer')}
                  onClick={onSkip}
                />
              )}
            </>
          )
        }
      >
        <IconButton component={DotsVerticalIcon} />
      </Dropdown>
    )
  }

  return (
    <S.Container>
      <div>{renderLeftSide()}</div>
      <S.Controls>
        {renderDropdown()}
        {renderCloseButton()}
      </S.Controls>
    </S.Container>
  )
}
